/**
 * Scrambler stream whitelist + per-stream key issuance, for the DRM console's
 * "Keys (LIVE)" flow.
 *
 * Whitelist management now goes DIRECTLY to the sig-control plane (controlApi,
 * /api/v1/streams, tenant resolved from the JWT) — the control plane mirrors the
 * address into the @aes authz set, so no @mgmt proxy and no serviceId in the
 * path. A stream id is the broadcast address `<app>/<name>`.
 *
 *   GET    /streams                 → list owned addresses
 *   POST   /streams {app,name}      → whitelist an address (kind defaults to live)
 *   DELETE /streams/{app}/{name}    → remove
 *
 * Key ISSUANCE stays on the @aes gateway (it holds the master secret) via the
 * @mgmt proxy, which injects ADMIN_API_KEY server-side:
 *   GET /scrambler?r=<app/name>     → { r, hex, base64 }
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { controlApi } from '@/api/client';
import { aesApi, aesPath, aesKeys } from './_shared';

// ---- Types ------------------------------------------------------------------

export interface AesScramblerStreamsResponse {
  streams: string[]; // broadcast addresses "<app>/<name>"
  count: number;
  redis_backed: boolean;
}

export interface AesScramblerKeyResponse {
  r: string;
  hex: string;
  base64: string;
}

// ---- Helpers ----------------------------------------------------------------

/** Split an `<app>/<name>` address into its segments for path/body building. */
function splitId(id: string): { app: string; name: string } {
  const i = id.indexOf('/');
  return i < 0 ? { app: id, name: '' } : { app: id.slice(0, i), name: id.slice(i + 1) };
}

function invalidateStreams(qc: ReturnType<typeof useQueryClient>, serviceId: string) {
  void qc.invalidateQueries({ queryKey: aesKeys.scrambler(serviceId) });
}

// ---- Hooks ------------------------------------------------------------------

export function useAesScramblerStreams(serviceId: string | undefined) {
  return useQuery<AesScramblerStreamsResponse>({
    queryKey: aesKeys.scrambler(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 5_000,
    retry: false,
    // sig-control returns { streams: [{ id, ... }] }; project to the address list.
    queryFn: async () => {
      const rows = (await controlApi.get<{ streams: Array<{ id: string }> }>('/streams')).data
        .streams ?? [];
      const ids = rows.map((s) => s.id);
      return { streams: ids, count: ids.length, redis_backed: true };
    },
  });
}

export function useAddScramblerStream(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: string) => {
      const { app, name } = splitId(r.trim());
      return controlApi.post('/streams', { app, name, kind: 'live' });
    },
    onSuccess: () => invalidateStreams(qc, serviceId),
  });
}

export function useRemoveScramblerStream(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: string) => {
      const { app, name } = splitId(r);
      return controlApi.delete(
        `/streams/${encodeURIComponent(app)}/${encodeURIComponent(name)}`,
      );
    },
    onSuccess: () => invalidateStreams(qc, serviceId),
  });
}

/**
 * Lazy mutation-style key issuance: call `mutate(r)` to fetch the hex+base64 key
 * for a whitelisted address. Served by the @aes gateway (master secret) via the
 * @mgmt proxy. Result lives in `data` after success.
 */
export function useIssueScramblerKey(serviceId: string) {
  return useMutation({
    mutationFn: async (r: string) =>
      (
        await aesApi.get<AesScramblerKeyResponse>(
          aesPath(serviceId, `/scrambler?r=${encodeURIComponent(r)}`),
        )
      ).data,
  });
}
